//
// # Wire Context Menu
//
// Displays wire information and actions when right-clicking on schematic wires.
// Wire label editing is handled via a modal dialog in the IDE (consistent with
// other edit dialogs like Rename Pin, Edit Instance Parameters, etc.).
//

import type { WireSegment } from "DrawCore/src/content/schematic/wire";
import { ContentKind } from "DrawCore/src/content/kind";
import { Palette } from "DrawCore/src/style";
import {
  BaseContextMenu,
  escapeHtml,
  type ContextMenuStyleConfig,
} from "./baseContextMenu";

export class WireContextMenu extends BaseContextMenu {
  // Note: Explicit constructor omitted per lint rule (@typescript-eslint/no-useless-constructor).
  // The base class constructor handles editor initialization.

  // The wire under the mouse pointer
  private currentWire: WireSegment | null = null;

  // -- Style config --

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "150px",
    maxWidth: "300px",
  };

  // -- Subclass stylesheet --

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textTarget};
      }
      .context-menu-detail {
        color: ${p.textMuted};
        font-size: 11px;
      }
    `;
  }

  // -- clearState --

  protected clearState(): void {
    this.currentWire = null;
  }

  // -- Show --

  // Show context menu for a wire at the given position.
  show = (wire: WireSegment, pageX: number, pageY: number): void => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this.currentWire = wire;

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content
    const content = this.buildMenuContent(wire);

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupMenuItemHandlers();
  };

  // Build the HTML content for the menu
  private buildMenuContent = (wire: WireSegment): string => {
    const labels = Array.from(wire.labels);
    const labelText =
      labels.length > 0
        ? labels.map((l) => l.inner.data.text).join(", ")
        : "(no label)";

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">Wire</div>`;
    content += `<div class="context-menu-detail">Label: ${escapeHtml(
      labelText
    )}</div>`;
    content += `</div>`;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="edit-label">Edit Label...</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete Wire</div>`;
    content += `</div>`;

    return content;
  };

  // Set up click handlers for menu items
  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.onclick = (e) => {
        e.stopPropagation();
        const action = item.getAttribute("data-action");

        if (action === "edit-label") {
          this.handleEditLabel();
        } else if (action === "delete") {
          this.handleDelete();
          this.hide();
        }
      };
    });
  };

  // Open the Edit Wire Label dialog for the given wire.
  // Can be called directly (e.g., from command palette) without showing the context menu.
  // Note: This edits only the first label if multiple exist. Multiple labels
  // on a single wire are rare; this UI focuses on the common single-label case.
  openEditDialog = (wire: WireSegment): void => {
    const { editor } = this;
    if (editor.isLocked) return;

    // Wire must have an ID to be editable
    const wireId = wire.data.id;
    if (wireId === undefined) return;

    // Get current label text (first label only)
    const labels = Array.from(wire.labels);
    const currentLabel = labels.length > 0 ? labels[0].inner.data.text : "";

    // Notify the platform to open the edit dialog
    editor.platform.notifyEditWireLabel(wireId, currentLabel);
  };

  // Handle edit label action from context menu
  private handleEditLabel = (): void => {
    const { currentWire } = this;
    if (!currentWire) return;

    this.openEditDialog(currentWire);
    this.hide();
  };

  // Handle delete action.
  // Deletes without confirmation, consistent with other entity deletions in the UI.
  private handleDelete = (): void => {
    const { editor, currentWire } = this;
    if (!currentWire || editor.isLocked) return;

    // Delete the wire through the schematic's delete mechanism
    if (editor.content.contentKind === ContentKind.Schematic) {
      // Explicitly select the wire before deleting to ensure we delete the right one
      editor.content.select(currentWire);
      editor.content.deleteSelected();
    }
  };
}
